import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { PROTECTED_DRAWER_CLASSNAME } from '../../hooks/useOnKeyPress';
import { usePortal } from '../../hooks/usePortal';
import { useAppwideDrawerContext } from '../../providers/AppwideDrawerProvider';
import { IconButton } from '../Button/IconButton';
import { Box, HStack, Stack } from '../Core';
import { Divider } from '../Divider';
import { Drawer } from '../Drawer';
import { FormControlSizes } from '../Form/types';
import { Icon, ICON_SIZES, IconName } from '../Icons';
import { Tab, TabAppearance, TabButton, TabList, Tabs, TabSize } from '../Tabs';
import { Text } from '../Text';

export const AppwideDrawer = () => {
  const { drawer, contentSpec } = useAppwideDrawerContext();

  // Whenever the user changes the spec, we guarantee a remount of the drawer content.
  // React / JSX is tricky when you are doing stuff like passing in a function to render a component across react life cycles.
  // Doing this just makes things simpler. There may very well be much better ways of handling this whole work flow.
  const contentSpecHash = useMemo(() => (contentSpec ? uuid() : undefined), [contentSpec]);

  const location = useLocation();
  // Flexible layout uses a wider gap between layout elements
  const adjustedMargin = location?.pathname.startsWith('/trading') ? '-spacingLayout' : '-spacing2';

  const { setPortalRef } = usePortal('drawer-portal');

  const renderHeader = () => {
    if (contentSpec?.drawerHeaderDesign === 'flex') {
      // Simple header with 12px padding, left-aligned title, right-aligned close button
      return (
        <Box
          p="spacing12"
          color="colors.gray.100"
          borderBottom="1px solid"
          borderColor="backgroundBody"
          background="backgroundDrawer"
        >
          <HStack justifyContent="space-between" alignItems="center" gap="spacing8" data-testid="app-wide-drawer-title">
            <Box minWidth={0} flex="1 1 auto" overflowX="hidden">
              {contentSpec?.flexHeaderTitle ? (
                contentSpec.flexHeaderTitle()
              ) : (
                <Text fontWeight="500">{contentSpec?.title}</Text>
              )}
            </Box>

            <HStack gap="spacing8" flex="0 0 auto">
              {contentSpec?.renderHeaderContent && contentSpec?.renderHeaderContent()}
              <IconButton
                icon={IconName.Close}
                size={FormControlSizes.Small}
                ghost
                onClick={() => drawer.close()}
                data-testid="app-wide-drawer-close-button"
              />
            </HStack>
          </HStack>
        </Box>
      );
    }

    // Default tab-based header (headerType === 'tab' or undefined)
    return (
      <HStack
        justifyContent="space-between"
        borderBottom="1px solid"
        borderColor="backgroundBody"
        background="backgroundFilledTabList"
      >
        <Tabs appearance={TabAppearance.Filled} size={TabSize.Small} selectedIndex={0}>
          <TabList>
            <Tab label={contentSpec?.title} data-testid="app-wide-drawer-title" />
          </TabList>
        </Tabs>
        <HStack>
          {contentSpec?.renderHeaderContent && contentSpec?.renderHeaderContent()}
          {contentSpec?.renderHeaderContent && <Divider orientation="vertical" />}
          <TabButton
            size={TabSize.Small}
            onClick={() => drawer.close()}
            suffix={<Icon icon={IconName.Close} size={ICON_SIZES.SMALL} />}
            appearance={TabAppearance.Filled}
            data-testid="app-wide-drawer-close-button"
          />
        </HStack>
      </HStack>
    );
  };

  const drawerClassName = drawer.className
    ? `${drawer.className} ${PROTECTED_DRAWER_CLASSNAME}`
    : PROTECTED_DRAWER_CLASSNAME;
  return (
    <Drawer
      {...drawer}
      drawerHeaderDesign={contentSpec?.drawerHeaderDesign}
      adjustForGapMargin={adjustedMargin}
      className={drawerClassName}
      data-testid="app-wide-drawer"
    >
      {renderHeader()}

      <Stack ref={setPortalRef} flex="1" key={contentSpecHash} style={{ overflowY: 'auto' }}>
        {contentSpec?.renderContent?.()}
      </Stack>
    </Drawer>
  );
};
